import type { RequestOptions } from '@@/plugin-request/request';
import { request } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';
import { getAccessToken } from '@/services/oauth2';

type ServiceKey = keyof typeof BizAppConfig.services;

const getTenantUuid = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const userRaw = localStorage.getItem('user');
    if (!userRaw) {
      return undefined;
    }
    const user = JSON.parse(userRaw);
    return user?.tenantUuid;
  } catch (_error) {
    return undefined;
  }
};

const getAuthHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  const tenantUuid = getTenantUuid();

  if (!token && !tenantUuid) {
    return {};
  }

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantUuid ? { 'x-tenant-id': tenantUuid } : {}),
  };
};

const normalizePath = (basePath: string, path: string): string => {
  const normalizedBasePath = basePath.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBasePath}${normalizedPath}`;
};

export const createServiceRequest = (serviceKey: ServiceKey) => {
  const service = BizAppConfig.services?.[serviceKey];
  if (!service) {
    throw new Error(
      `[createServiceRequest] Missing service config for "${String(
        serviceKey,
      )}". Please configure BizAppConfig.services.${String(
        serviceKey,
      )} in config/application.manifest.ts.`,
    );
  }

  return <T = any>(path: string, options?: RequestOptions) => {
    const requestPath = normalizePath(service.basePath, path);

    return request<T>(requestPath, {
      baseURL: service.baseUrl,
      timeout: service.timeout,
      headers: {
        ...getAuthHeaders(),
        ...(options?.headers || {}),
      },
      ...(options || {}),
    });
  };
};
